/**
 * Safe mode screen.
 *
 * Shown by <App /> when `readSafeMode()` returns non-null — i.e. the
 * watchdog gave up after MAX_CONSECUTIVE_BOOT_FAILURES boots in a row
 * or a reload-storm. The screen does no work: no API calls, no plan
 * fetch, no media cache reads. Just a static message + two buttons.
 *
 * Why a separate screen instead of just showing an error?
 *   - Whatever was crashing on boot can't crash this screen, because
 *     this screen never touches any of the failing subsystems.
 *   - Gives a human at the venue (or an operator via remote viewing)
 *     a clear "this thing is broken, here's what to try" signal
 *     instead of an infinite crash loop.
 *   - Provides a deliberate path to factory reset without needing the
 *     diagnostic triple-tap (which itself requires React state).
 */

import { useEffect, useState } from 'react';
import {
  clearSafeMode,
  readSafeMode,
  type SafeModeFlag,
} from '@/health/watchdog';

interface SafeScreenProps {
  /** Called when user taps "Try again". Parent should re-mount <App />. */
  onRetry: () => void;
  /** Called when user taps "Factory reset". Parent should wipe state. */
  onFactoryReset: () => void;
}

export default function SafeScreen({ onRetry, onFactoryReset }: SafeScreenProps) {
  const [flag, setFlag] = useState<SafeModeFlag | null>(null);
  const [clock, setClock] = useState<string>('');

  useEffect(() => {
    setFlag(readSafeMode());
    const tick = () => {
      const d = new Date();
      setClock(
        `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`,
      );
    };
    tick();
    const t = setInterval(tick, 30_000);
    return () => clearInterval(t);
  }, []);

  const handleRetry = () => {
    clearSafeMode();
    onRetry();
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.brand}>SENSAR PLAYER</div>
        <div style={styles.title}>SAFE MODE</div>
        <div style={styles.detail}>
          The player exceeded its automatic recovery budget. Pick an action below.
        </div>
        {flag && (
          <div style={styles.meta}>
            reason: {flag.reason} · {new Date(flag.ts).toLocaleString()}
          </div>
        )}
        <div style={styles.actions}>
          <button style={styles.primary} onClick={handleRetry}>
            Try again
          </button>
          <button
            style={styles.danger}
            onClick={onFactoryReset}
          >
            Factory reset
          </button>
        </div>
        <div style={styles.footer}>{clock}</div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'fixed',
    inset: 0,
    background: '#0A0A0A',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  card: {
    minWidth: 480,
    padding: 48,
    borderRadius: 20,
    background: '#111111',
    border: '1px solid rgba(255,107,43,0.3)',
    textAlign: 'center',
  },
  brand: {
    fontSize: 11,
    letterSpacing: '0.3em',
    color: '#FF6B2B',
    marginBottom: 16,
    fontWeight: 600,
  },
  title: {
    fontSize: 32,
    fontWeight: 700,
    color: '#fff',
    marginBottom: 16,
    letterSpacing: '0.05em',
  },
  detail: {
    fontSize: 14,
    color: '#888',
    lineHeight: 1.5,
    marginBottom: 12,
  },
  meta: {
    fontSize: 11,
    color: '#555',
    fontFamily: 'monospace',
    marginBottom: 32,
  },
  actions: {
    display: 'flex',
    gap: 12,
    justifyContent: 'center',
    marginBottom: 24,
  },
  primary: {
    padding: '12px 24px',
    borderRadius: 10,
    border: 'none',
    background: '#FF6B2B',
    color: '#fff',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  danger: {
    padding: '12px 24px',
    borderRadius: 10,
    border: '1px solid #7F1D1D',
    background: 'transparent',
    color: '#EF4444',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
  },
  footer: {
    fontSize: 11,
    color: '#444',
    fontFamily: 'monospace',
    letterSpacing: '0.2em',
  },
};
